import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { fillEmptySeoDraft, improveSeoDraft, regenerateSeoDraft } from "@/lib/product-seo";
import { inputCls, labelCls, type ProductRow } from "../types";

interface Props {
  product: ProductRow;
  orgId: string;
  categoryName?: string | null;
  primaryImageUrl?: string | null;
  optionNames?: string[];
  optionValues?: string[];
  optionGroups?: Record<string, string[]>;
}

export default function StorefrontTab({ product, orgId, categoryName, primaryImageUrl, optionNames = [], optionValues = [], optionGroups = {} }: Props) {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState({
    seo_title: product.seo_title ?? "",
    meta_description: product.meta_description ?? "",
    seo_keywords: product.seo_keywords ?? "",
    url_slug: product.url_slug ?? "",
    og_title: product.og_title ?? "",
    og_description: product.og_description ?? "",
    og_image_url: product.og_image_url ?? primaryImageUrl ?? "",
    storefront_visible: product.storefront_visible !== false,
    is_featured: product.is_featured === true,
    min_purchase_qty: product.min_purchase_qty != null ? String(product.min_purchase_qty) : "",
    max_purchase_qty: product.max_purchase_qty != null ? String(product.max_purchase_qty) : "",
  });

  const save = async () => {
    setSaving(true);
    setError("");
    setNotice("");
    const { error: updErr } = await supabase.from("products").update({
      seo_title: form.seo_title.trim() || null,
      meta_description: form.meta_description.trim() || null,
      seo_keywords: form.seo_keywords.trim() || null,
      url_slug: form.url_slug.trim() || null,
      og_title: form.og_title.trim() || null,
      og_description: form.og_description.trim() || null,
      og_image_url: form.og_image_url.trim() || null,
      storefront_visible: form.storefront_visible,
      is_featured: form.is_featured,
      min_purchase_qty: form.min_purchase_qty ? Number(form.min_purchase_qty) : null,
      max_purchase_qty: form.max_purchase_qty ? Number(form.max_purchase_qty) : null,
    }).eq("id", product.id).eq("org_id", orgId);
    setSaving(false);
    if (updErr) {
      setError(updErr.message);
      return;
    }
    setNotice("SEO and storefront settings saved. They publish from the next approved listing snapshot.");
    queryClient.invalidateQueries({ queryKey: ["product-detail", orgId, product.id] });
  };

  const seoFacts = {
    name: product.name,
    brand: product.brand,
    category: categoryName ?? product.categories?.name,
    options: optionNames,
    values: optionValues,
    optionGroups,
  };

  const currentSeoDraft = () => ({
    seoTitle: form.seo_title,
    metaDescription: form.meta_description,
    seoKeywords: form.seo_keywords,
    urlSlug: form.url_slug,
  });

  const applySeoDraft = (
    next: { seoTitle: string; metaDescription: string; seoKeywords: string; urlSlug: string },
    notice: string,
    syncSocial = false,
  ) => {
    setForm((f) => ({
      ...f,
      seo_title: next.seoTitle,
      meta_description: next.metaDescription,
      seo_keywords: next.seoKeywords,
      url_slug: next.urlSlug,
      og_title: syncSocial ? next.seoTitle : (f.og_title.trim() ? f.og_title : product.name),
      og_description: syncSocial ? next.metaDescription : (f.og_description.trim() ? f.og_description : next.metaDescription),
    }));
    setNotice(notice);
  };

  const generateDraft = () => {
    applySeoDraft(
      fillEmptySeoDraft(currentSeoDraft(), seoFacts),
      "Filled empty SEO fields only. Existing operator values were kept.",
    );
  };

  const improveDraft = () => {
    applySeoDraft(
      improveSeoDraft(currentSeoDraft(), seoFacts),
      "SEO and social metadata polished from grounded product facts. Existing URL slug was preserved.",
      true,
    );
  };

  const regenerateDraft = () => {
    applySeoDraft(
      regenerateSeoDraft(currentSeoDraft(), seoFacts),
      "SEO and social metadata regenerated from grounded product facts. Existing URL slug was preserved.",
      true,
    );
  };

  const previewTitle = form.seo_title || product.name;
  const previewDesc = form.meta_description || product.short_description || "";
  const previewUrl = form.url_slug ? `store.example/${form.url_slug.replace(/^\/+/, "")}` : "store.example/…";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">SEO &amp; Storefront</h2>
          <p className="text-xs text-slate-500 mt-0.5">Canonical search and social metadata. Brand, MPN, and GTIN stay under Product Data / Identifiers.</p>
        </div>
        <div className="flex gap-2">
          <button type="button" data-testid="seo-generate-draft" onClick={generateDraft} className="px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50">
            Fill empty SEO
          </button>
          <button type="button" data-testid="seo-improve" onClick={improveDraft} className="px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50">
            Improve SEO
          </button>
          <button type="button" data-testid="seo-regenerate" onClick={regenerateDraft} className="px-3 py-2 text-sm border border-amber-200 text-amber-800 rounded-lg hover:bg-amber-50">
            Regenerate SEO
          </button>
          <button type="button" data-testid="seo-save" onClick={save} disabled={saving} className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-lg disabled:opacity-60">
            {saving ? "Saving…" : "Save SEO"}
          </button>
        </div>
      </div>
      {error && <p className="text-sm text-rose-600">{error}</p>}
      {notice && <p className="text-sm text-emerald-700">{notice}</p>}

      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
        <p className="text-xs font-semibold text-slate-500 mb-2">Search preview</p>
        <p className="text-sm text-blue-800 font-medium" data-testid="seo-search-preview-title">{previewTitle}</p>
        <p className="text-xs text-emerald-700">{previewUrl}</p>
        <p className="text-xs text-slate-600 mt-1">{previewDesc}</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="seo-title">SEO / page title</label>
          <input id="seo-title" data-testid="seo-title" value={form.seo_title} onChange={(e) => setForm((f) => ({ ...f, seo_title: e.target.value }))} className={inputCls} maxLength={70} />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="seo-meta">Meta description</label>
          <textarea id="seo-meta" data-testid="seo-meta-description" value={form.meta_description} onChange={(e) => setForm((f) => ({ ...f, meta_description: e.target.value }))} className={inputCls} rows={3} maxLength={320} />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="seo-keywords">Search / meta keywords</label>
          <input id="seo-keywords" data-testid="seo-keywords" value={form.seo_keywords} onChange={(e) => setForm((f) => ({ ...f, seo_keywords: e.target.value }))} className={inputCls} placeholder="PVC bridle, horse bridle, rubber reins" />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="seo-slug">URL slug</label>
          <input id="seo-slug" data-testid="seo-url-slug" value={form.url_slug} onChange={(e) => setForm((f) => ({ ...f, url_slug: e.target.value }))} className={inputCls} placeholder="pvc-bridle-padding-rubber-reins" />
        </div>
      </div>

      <div className="border-t border-slate-200 pt-4 space-y-3">
        <p className="text-xs font-semibold text-slate-600">Social sharing (Open Graph)</p>
        <p className="text-xs text-slate-500">BigCommerce uses product images when Open Graph image is enabled. A custom OG image URL is stored here for preview only.</p>
        <div>
          <label className={labelCls}>Open Graph title</label>
          <input data-testid="seo-og-title" value={form.og_title} onChange={(e) => setForm((f) => ({ ...f, og_title: e.target.value }))} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Open Graph description</label>
          <textarea data-testid="seo-og-description" value={form.og_description} onChange={(e) => setForm((f) => ({ ...f, og_description: e.target.value }))} className={inputCls} rows={2} />
        </div>
        <div>
          <label className={labelCls}>Open Graph image URL (preview / canonical)</label>
          <input data-testid="seo-og-image" value={form.og_image_url} onChange={(e) => setForm((f) => ({ ...f, og_image_url: e.target.value }))} className={inputCls} />
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-3 max-w-md">
          <p className="text-xs text-slate-400 mb-1">Social preview</p>
          {form.og_image_url ? <img src={form.og_image_url} alt="" className="w-full h-28 object-cover rounded mb-2 bg-slate-100" /> : <div className="w-full h-20 bg-slate-100 rounded mb-2" />}
          <p className="text-sm font-medium text-slate-800">{form.og_title || previewTitle}</p>
          <p className="text-xs text-slate-600">{form.og_description || previewDesc}</p>
        </div>
      </div>

      <div className="border-t border-slate-200 pt-4 grid sm:grid-cols-2 gap-4">
        <p className="sm:col-span-2 text-xs font-semibold text-slate-600">Merchandising</p>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" data-testid="seo-visible" checked={form.storefront_visible} onChange={(e) => setForm((f) => ({ ...f, storefront_visible: e.target.checked }))} />
          Visible on storefront
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" data-testid="seo-featured" checked={form.is_featured} onChange={(e) => setForm((f) => ({ ...f, is_featured: e.target.checked }))} />
          Featured
        </label>
        <div>
          <label className={labelCls}>Minimum purchase quantity</label>
          <input data-testid="seo-min-qty" type="number" min={0} value={form.min_purchase_qty} onChange={(e) => setForm((f) => ({ ...f, min_purchase_qty: e.target.value }))} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Maximum purchase quantity</label>
          <input data-testid="seo-max-qty" type="number" min={0} value={form.max_purchase_qty} onChange={(e) => setForm((f) => ({ ...f, max_purchase_qty: e.target.value }))} className={inputCls} />
        </div>
      </div>
    </div>
  );
}
